// Crayo, Landing page (/)
import React from 'react';

// Cross-module symbols defined in Shared.jsx (loaded first by src/main.jsx).
// Pulling them from window preserves the pre-Vite authoring pattern without
// rewriting every component into a proper module graph.
const { Icon, Page, navigate, useToast } = window;

const { useState: useStateL, useEffect: useEffectL, useRef: useRefL } = React;

// --- BEFORE/AFTER scrubber ---
function BeforeAfterScrubber() {
  const [pos, setPos] = useStateL(50);
  const [interacted, setInteracted] = useStateL(false);
  const wrapRef = useRefL(null);
  const rafRef = useRefL(null);

  // auto-animate until user interacts
  useEffectL(() => {
    if (interacted) return;
    let start = performance.now();
    const tick = (t) => {
      const elapsed = (t - start) / 1000;
      // smooth sine: 3s loop, range 28 → 72
      const p = 50 + Math.sin(elapsed * (Math.PI * 2 / 3)) * 22;
      setPos(p);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [interacted]);

  const updateFromEvent = (clientX) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const p = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(p);
  };
  const onPointerDown = (e) => {
    setInteracted(true);
    updateFromEvent(e.clientX);
    const move = (ev) => updateFromEvent(ev.clientX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div
      ref={wrapRef}
      onPointerDown={onPointerDown}
      role="slider"
      aria-label="Compară fotografie și pagină de colorat"
      aria-valuenow={Math.round(pos)}
      tabIndex={0}
      onKeyDown={(e) => {
        setInteracted(true);
        if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 4));
        if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + 4));
      }}
      style={{
        position: 'relative',
        aspectRatio: '4/3',
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid var(--soft)',
        cursor: 'ew-resize',
        userSelect: 'none',
        touchAction: 'none',
        background: 'var(--paper)',
      }}
    >
      <img src="assets/sketches/hero.png" alt="Pagină de colorat generată" draggable="false"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: 'var(--paper)' }} />
      <div style={{ position: 'absolute', inset: 0, clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src="assets/photos/hero-4.jpg" alt="Fotografie originală" draggable="false"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
      {/* divider */}
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, width: 2, background: 'var(--paper)', boxShadow: '0 0 0 1px rgba(26,26,26,0.25)' }} />
      <div style={{
        position: 'absolute', top: '50%', left: `${pos}%`, transform: 'translate(-50%, -50%)',
        width: 44, height: 44, borderRadius: '50%', background: 'var(--paper)',
        border: '1.5px solid var(--ink)', display: 'grid', placeItems: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M8 6 L 4 12 L 8 18 M 16 6 L 20 12 L 16 18" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {/* labels */}
      <div style={{ position: 'absolute', top: 14, left: 14, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'rgba(253,249,243,0.9)', padding: '4px 10px', borderRadius: 999 }}>Înainte</div>
      <div style={{ position: 'absolute', top: 14, right: 14, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'rgba(253,249,243,0.9)', padding: '4px 10px', borderRadius: 999 }}>După</div>
    </div>
  );
}

// --- Hero ---
function Hero() {
  return (
    <section style={{ paddingTop: 72, paddingBottom: 96 }}>
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 1fr)', gap: 64, alignItems: 'center' }} className="hero-grid">
          <div>
            <div className="eyebrow">
              <Icon.Star size={12} /> &nbsp;Made in România
            </div>
            <h1 style={{ marginBottom: 20 }}>
              Cartea ta de colorat,<br />
              <span style={{ fontStyle: 'italic', fontWeight: 400 }}>din pozele voastre.</span>
            </h1>
            <p style={{ fontSize: 18, color: 'var(--muted)', maxWidth: 520, marginBottom: 32, lineHeight: 1.55 }}>
              Încarcă pozele copilului tău, primești cartea ta de colorat în PDF în 2 minute.
              <br />
              <span style={{ color: 'var(--muted)', textDecoration: 'line-through', marginRight: 6 }}>19,90 lei</span>
              <strong style={{ color: 'var(--ink)' }}>9,90 lei (-50%).</strong> Garanție 100% — banii înapoi dacă nu îți place.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <a href="#/create" onClick={(e)=>{e.preventDefault();navigate('/create');}} className="btn btn-primary btn-lg">
                Încarcă pozele tale gratuit <Icon.Arrow />
              </a>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Vezi previzualizarea înainte să plătești · fără cont</span>
            </div>
            {/* trust row */}
            <div style={{ display: 'flex', gap: 24, marginTop: 40, flexWrap: 'wrap', color: 'var(--muted)', fontSize: 13 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon.Shield /> Plată securizată (Stripe)</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon.Infinity /> Fără abonament, fără cont</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon.Check /> Garanție 100%</span>
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <BeforeAfterScrubber />
            {/* decorative doodles */}
            <div style={{ position: 'absolute', top: -28, right: -12, opacity: 0.8 }}>
              <Icon.Balloon size={56} />
            </div>
            <div style={{ position: 'absolute', bottom: -24, left: -24, opacity: 0.7 }}>
              <Icon.Star size={36} />
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
        }
      `}</style>
    </section>
  );
}

// --- How it works ---
function HowItWorks() {
  const steps = [
    { n: '01', icon: <Icon.Phone />, title: 'Încarci pozele tale', body: 'Alegi cele mai dragi amintiri de pe telefon sau laptop. Până la 20 de poze.' },
    { n: '02', icon: <Icon.Pencil />, title: 'Le transformăm în pagini de colorat', body: 'În câteva minute, fiecare poză devine o schiță în alb-negru, optimizată pentru hârtie A4.' },
    { n: '03', icon: <Icon.Crayons />, title: 'Le printezi acasă, copilul le colorează', body: 'Un PDF gata de printat ajunge pe email. Tu îl tipărești, copilul îl colorează.' },
  ];
  return (
    <section id="how" style={{ background: '#FBF5EA' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div className="eyebrow">Cum funcționează</div>
          <h2>Trei pași, nimic complicat.</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32 }} className="how-grid">
          {steps.map((s) => (
            <div key={s.n} style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div className="doodle">{s.icon}</div>
                <span style={{ fontFamily: 'Fraunces, serif', fontSize: 44, color: 'var(--soft)', fontWeight: 500 }}>{s.n}</span>
              </div>
              <h3 style={{ marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: 'var(--muted)', fontSize: 15 }}>{s.body}</p>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 800px) {
            .how-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          }
        `}</style>
      </div>
    </section>
  );
}

// --- Examples gallery ---
// Side-by-side photo + sketch in square cells. Both use object-fit: cover
// with per-image object-position so the subject stays in frame after cropping.
function Examples() {
  const items = [
    { label: 'Vara la bunici',        photo: 'assets/photos/01-bunici-2.jpg',    sketch: 'assets/sketches/01-bunici.png',    photoPos: '65% 45%', sketchPos: '60% 50%' },
    { label: 'Max, câinele nostru',   photo: 'assets/photos/02-max-1.jpg',       sketch: 'assets/sketches/02-max.png',       photoPos: '50% 40%', sketchPos: '50% 40%' },
    { label: 'Ziua ei de naștere',    photo: 'assets/photos/03-ziua-2.jpg',      sketch: 'assets/sketches/03-ziua.png',      photoPos: '50% 55%', sketchPos: '50% 55%' },
    { label: 'La plajă',              photo: 'assets/photos/04-plaja-2.jpg',     sketch: 'assets/sketches/04-plaja.png',     photoPos: '55% 45%', sketchPos: '50% 50%' },
    { label: 'În parc cu tata',       photo: 'assets/photos/05-parc-4.jpg',      sketch: 'assets/sketches/05-parc.png',      photoPos: '50% 62%', sketchPos: '50% 55%', photoZoom: 1.35 },
    { label: 'Prima zi de grădiniță', photo: 'assets/photos/06-gradinita-1.jpg', sketch: 'assets/sketches/06-gradinita.png', photoPos: '50% 35%', sketchPos: 'center top' },
  ];
  return (
    <section id="examples">
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div className="eyebrow">Exemple</div>
          <h2>Momente care devin pagini.</h2>
          <p style={{ color: 'var(--muted)', maxWidth: 520, margin: '16px auto 0' }}>
            Fiecare poză păstrează atmosfera originalului, doar contururile rămân, ca un cadou gata de colorat.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 28 }} className="ex-grid">
          {items.map(({ label, photo, sketch, photoPos, sketchPos, photoZoom }, i) => (
            <div key={i} className="card card-hover" style={{ padding: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ aspectRatio: '1/1', borderRadius: 10, overflow: 'hidden', background: 'var(--soft)' }}>
                  <img src={photo} alt={`Fotografie, ${label}`} loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: photoPos || 'center', display: 'block', transform: photoZoom ? `scale(${photoZoom})` : undefined, transformOrigin: photoPos || 'center' }} />
                </div>
                <div style={{ aspectRatio: '1/1', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--soft)', background: 'var(--paper)' }}>
                  <img src={sketch} alt={`Pagină de colorat, ${label}`} loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: sketchPos || 'center', display: 'block' }} />
                </div>
              </div>
              <div style={{ padding: '16px 4px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Fraunces, serif', fontSize: 19, fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'ui-monospace, monospace' }}>#{String(i+1).padStart(2,'0')}</span>
              </div>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 720px) { .ex-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    </section>
  );
}

// --- Pricing ---
// Single flat offer (replaces the prior 3/10/20 tier ladder). The decision
// to flatten is documented in .planning/pricing-pivot-2026-05-04.md: optimizing
// for first-paying-user count over AOV, fewer decisions, lower friction, single
// price visible above the fold.
function Pricing() {
  return (
    <section id="pricing" style={{ background: '#FBF5EA' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div className="eyebrow">Preț</div>
          <h2>Plătești o dată. Atât.</h2>
          <p style={{ color: 'var(--muted)', maxWidth: 520, margin: '16px auto 0' }}>
            Un preț fix, simplu. Fără abonament, fără cont, fără surprize.
          </p>
        </div>
        {/* No card framing here — a card implies "one option among many",
            which is the opposite of what we want. The price stands on
            its own, centered on the section background. */}
        <div style={{ textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
          {/* Discount eyebrow pill — terracotta on paper, scannable in 200ms */}
          <div style={{ display: 'inline-block', padding: '4px 14px', background: 'var(--terracotta)', color: 'var(--paper)', borderRadius: 999, fontSize: 13, fontWeight: 600, letterSpacing: '0.06em', marginBottom: 14 }}>
            -50%
          </div>
          {/* Anchor price (strikethrough) above the actual price */}
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 28, fontWeight: 400, color: 'var(--muted)', textDecoration: 'line-through', lineHeight: 1, marginBottom: 6 }}>
            19,90 lei
          </div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 88, fontWeight: 500, lineHeight: 1, letterSpacing: '-0.03em', color: 'var(--ink)', marginBottom: 12 }}>
            9,90 lei
          </div>
          <p style={{ color: 'var(--terracotta)', fontSize: 15, fontWeight: 500, marginBottom: 32 }}>
            Garanție 100% — banii înapoi dacă nu îți place
          </p>
          <ul style={{ listStyle: 'none', display: 'inline-flex', flexDirection: 'column', gap: 12, marginBottom: 36, textAlign: 'left', paddingLeft: 0 }}>
            <li style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 16 }}><Icon.Check /> Până la 20 de pagini de colorat</li>
            <li style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 16 }}><Icon.Check /> PDF printabil pe hârtie A4</li>
            <li style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 16 }}><Icon.Check /> Livrare instant pe email</li>
            <li style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 16 }}><Icon.Check /> Vezi previzualizările înainte să plătești</li>
          </ul>
          <div>
            <a href="#/create" onClick={(e)=>{e.preventDefault();navigate('/create');}} className="btn btn-primary btn-lg">
              Începe cartea ta
            </a>
          </div>
        </div>
        <div style={{ marginTop: 36, padding: '20px 28px', background: 'var(--paper)', borderRadius: 12, border: '1px solid var(--soft)', display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap', fontSize: 14 }} className="price-trust">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon.Check /> PDF printabil A4</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon.Check /> Fără cont, fără abonament</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon.Check /> Plată securizată prin Stripe</span>
        </div>
        <p style={{ textAlign: 'center', marginTop: 6, fontSize: 13, color: 'var(--muted)' }}>
          Variantă tipărită și livrată, în curând.
        </p>
        <style>{`
          @media (max-width: 900px) {
            .price-trust { flex-direction: column; align-items: flex-start !important; gap: 10px !important; }
          }
        `}</style>
      </div>
    </section>
  );
}

// --- Testimonials ---
function Testimonials() {
  const items = [
    { name: 'Ana M.', city: 'Cluj', quote: 'Am făcut o carte cu toate pozele din vacanța la bunici. Fiul meu a colorat fiecare pagină în două zile. Vrea alta.' },
    { name: 'Mihai D.', city: 'București', quote: 'Cadou de ziua fiicei mele, a recunoscut imediat grădina bunicilor în conturul desenului. A plâns puțin, apoi a colorat tot.' },
    { name: 'Ruxandra P.', city: 'Iași', quote: 'Nu credeam că o să iasă atât de curat. Poza cu Max, cățelul nostru, a ieșit perfectă. A costat cât două cafele.' },
  ];
  return (
    <section>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div className="eyebrow">Părinți care au încercat</div>
          <h2>Cadouri care rămân.</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }} className="testim-grid">
          {items.map((t, i) => (
            <figure key={i} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <svg width="28" height="20" viewBox="0 0 28 20" fill="none">
                <path d="M10 0 C 4 4 1 10 1 18 M 10 20 L 1 20 L 1 10 L 10 10 Z M 26 0 C 20 4 17 10 17 18 M 26 20 L 17 20 L 17 10 L 26 10 Z"
                  stroke="#C65D3B" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
              <blockquote style={{ fontFamily: 'Fraunces, serif', fontSize: 19, lineHeight: 1.4, fontWeight: 400, letterSpacing: '-0.01em' }}>
                „{t.quote}"
              </blockquote>
              <figcaption style={{ fontSize: 14, color: 'var(--muted)', marginTop: 'auto' }}>
                <strong style={{ color: 'var(--ink)', fontWeight: 500 }}>{t.name}</strong> · {t.city}
              </figcaption>
            </figure>
          ))}
        </div>
        <style>{`
          @media (max-width: 900px) { .testim-grid { grid-template-columns: 1fr !important; max-width: 520px; margin: 0 auto; } }
        `}</style>
      </div>
    </section>
  );
}

// --- FAQ ---
function FAQ() {
  const [open, setOpen] = useStateL(0);
  const items = [
    { q: 'Ce se întâmplă cu pozele mele după ce le încarc?',
      a: 'Pozele originale sunt șterse automat după 72 de ore de pe serverele noastre. PDF-ul generat rămâne al tău, îl descarci din link-ul primit pe email timp de 30 de zile. Nu folosim pozele pentru altceva decât pentru a genera pagina ta de colorat. Nu le publicăm, nu le antrenăm pe modele, nu le vindem niciodată.' },
    { q: 'Pot să printez paginile acasă?',
      a: 'Da, sunt optimizate pentru hârtie A4, alb-negru, și merg pe orice imprimantă obișnuită. Recomandăm 90 g/m² sau mai groasă, mai ales dacă vrei să colorezi cu creioane cerate sau acuarele.' },
    { q: 'Ce fac dacă nu-mi place rezultatul?',
      a: 'Prima pagină e gratuită, exact ca să vezi calitatea înainte să plătești. Iar dacă după cumpărare tot nu ești mulțumit, scrie-ne la salut@crayo.ro în maxim 14 zile și îți returnăm integral suma plătită.' },
    { q: 'Merge și pentru poze cu animale de companie?',
      a: 'Da, orice subiect clar vizibil în poză funcționează, câini, pisici, iepurași, broaște țestoase. Pozele făcute pe lumină naturală, cu subiectul în prim-plan, dau cele mai bune rezultate.' },
    { q: 'Când o să fie disponibilă varianta tipărită și livrată?',
      a: 'Lucrăm la ea, lasă-ne emailul în formularul de mai jos și te anunțăm primul. Varianta tipărită va veni legată ca o cărticică, pe hârtie groasă, livrată prin curier în România.' },
    { q: 'Pot primi un pachet personalizat?',
      a: 'Da. Pentru cazuri speciale (cadouri mai mari, evenimente, pachete cu mai multe teme), scrie-ne la salut@crayo.ro și facem unul pe măsură.' },
  ];
  return (
    <section id="faq" style={{ background: '#FBF5EA' }}>
      <div className="container-narrow">
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div className="eyebrow">Întrebări</div>
          <h2>Lucrurile pe care le întreabă cei mai mulți părinți.</h2>
        </div>
        <div>
          {items.map((it, i) => (
            <div key={i} style={{ borderBottom: '1px solid var(--soft)' }}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                style={{ width: '100%', padding: '22px 4px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, fontFamily: 'Fraunces, serif', fontSize: 19, fontWeight: 500, color: 'var(--ink)', letterSpacing: '-0.01em' }}
                aria-expanded={open === i}
              >
                <span style={{ textWrap: 'balance' }}>{it.q}</span>
                <span style={{ flexShrink: 0, transition: 'transform 200ms ease', transform: open === i ? 'rotate(180deg)' : 'rotate(0)' }}>
                  <Icon.ChevronDown />
                </span>
              </button>
              <div style={{
                overflow: 'hidden',
                maxHeight: open === i ? 300 : 0,
                transition: 'max-height 320ms ease',
              }}>
                <p style={{ padding: '0 4px 22px', color: 'var(--muted)', fontSize: 15, lineHeight: 1.65, maxWidth: 620 }}>{it.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Waitlist strip ---
function WaitlistStrip() {
  const [email, setEmail] = useStateL('');
  const [submitted, setSubmitted] = useStateL(false);
  const [busy, setBusy] = useStateL(false);
  const toast = useToast();
  const submit = async (e) => {
    e.preventDefault();
    if (busy || submitted) return;
    if (!email || !email.includes('@')) {
      toast.push('Verifică adresa de email.');
      return;
    }
    setBusy(true);
    try {
      const res = await window.joinWaitlist(email, 'print');
      toast.push(res?.alreadyOnList
        ? 'Ești deja pe listă — îți confirmăm primii când lansăm.'
        : 'Mulțumim! Te anunțăm primii.');
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      toast.push(err?.message || 'Nu am reușit să te înscriem. Reîncearcă în câteva secunde.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section style={{ padding: '72px 0' }}>
      <div className="container">
        <div className="card waitlist-card" style={{ padding: 48, background: 'var(--ink)', color: 'var(--paper)', borderColor: 'var(--ink)', display: 'grid', gridTemplateColumns: '1fr auto', gap: 32, alignItems: 'center' }}>
          <div>
            <h2 style={{ color: 'var(--paper)', marginBottom: 8 }}>
              Vrei să afli când lansăm varianta tipărită?
            </h2>
            <p style={{ color: 'rgba(253,249,243,0.7)', fontSize: 15, marginBottom: 0 }}>
              Cărticică legată, hârtie groasă, livrată prin curier. Fără spam, un singur email.
            </p>
          </div>
          <form onSubmit={submit} style={{ display: 'flex', gap: 8, minWidth: 360 }} className="waitlist-form">
            <input type="email" className="input" placeholder="email@exemplu.ro" value={email}
              onChange={(e)=>setEmail(e.target.value)} style={{ background: 'rgba(253,249,243,0.08)', color: 'var(--paper)', borderColor: 'rgba(253,249,243,0.2)', flex: 1 }} />
            <button type="submit" className="btn btn-primary" disabled={busy || submitted}>
              {submitted ? 'Înscris ✓' : busy ? 'Se trimite…' : 'Anunță-mă'}
            </button>
          </form>
        </div>
      </div>
      <style>{`
        @media (max-width: 800px) {
          .waitlist-card {
            grid-template-columns: 1fr !important;
            padding: 28px !important;
            gap: 20px !important;
          }
          .waitlist-card h2 { font-size: 24px !important; }
          .waitlist-form {
            flex-direction: column !important;
            min-width: 0 !important;
            width: 100%;
          }
          .waitlist-form .input,
          .waitlist-form .btn { width: 100%; }
        }
      `}</style>
    </section>
  );
}

// --- Landing page ---
function LandingPage() {
  return (
    <Page>
      <Hero />
      <HowItWorks />
      <Examples />
      <Pricing />
      {/* <Testimonials /> hidden until we have real ones — re-add this line when ready */}
      <FAQ />
      <WaitlistStrip />
    </Page>
  );
}

Object.assign(window, { LandingPage });
